import { format } from "date-fns";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Tooltip, Typography } from "@mui/material";

import APP_ROUTES from "../../../../../routers/routeConstants";

import RangeDatePicker from "../../../../../shared/RangeDatePicker";

import { useGetJobsSummary } from "../../../api/queries/useGetJobsSummary";
import {
  ToolTipIcon,
  TotalJobsCardIcon,
  TotalApplicantsCardIcon,
  TotalJobOpeningsCardIcon,
} from "../../../assets/icons/jobs-overview-page-icons";

const JobsSummary = () => {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const location = useLocation();
  const navigate = useNavigate();

  const isDashboardRoute = location.pathname === APP_ROUTES.DASHBOARD;
  const handleNavigateToCreateJob = () => {
    navigate(APP_ROUTES.CREATE_JOB);
  };
  const { t } = useTranslation("goHire", { keyPrefix: "overviewPage" });

  const filters = useMemo(
    () => ({
      from: dateRange[0] ? format(dateRange[0], "yyyy-MM-dd") : undefined,
      to: dateRange[1] ? format(dateRange[1], "yyyy-MM-dd") : undefined,
    }),
    [dateRange]
  );

  const { data, isLoading } = useGetJobsSummary(filters);

  const summaryCards = [
    {
      icon: <TotalJobsCardIcon />,
      value: data?.totals.totalJobs ?? 0,
      title: t("totalJobs"),
      tooltip: t("totalNumberJobs"),
      background: "linear-gradient(292deg, rgba(3, 145, 3, 0.20) -125.56%, rgba(255, 255, 255, 0.20) 74.08%), #FFF",
    },
    {
      icon: <TotalApplicantsCardIcon />,
      value: data?.totals.totalApplicants ?? 0,
      title: t("totalApplicants"),
      tooltip: t("totalNumberApplicants"),
      background: "linear-gradient(292deg, rgba(44, 77, 237, 0.20) -132.72%, rgba(255, 255, 255, 0.20) 74.08%), #FFF",
    },
    {
      icon: <TotalJobOpeningsCardIcon />,
      value: data?.totals.jobsOpening ?? 0,
      title: t("jobsOpening"),
      tooltip: t("totalNumberOpenJobs"),
      background: "linear-gradient(291deg, rgba(157, 0, 236, 0.20) -119.29%, rgba(255, 255, 255, 0.20) 74.13%), #FFF",
    },
  ];

  if (isLoading) <></>;
  return (
    <Box sx={styles.container}>
      <Box sx={styles.jobsSummaryContainer}>
        <Typography sx={styles.jobsSummaryTitle}>{t("jobsSummary")}</Typography>
        <Box sx={{ display: "flex", gap: "8px" }}>
          {isDashboardRoute && (
            <Button variant="outlined-sm" onClick={handleNavigateToCreateJob}>
              {t("createJob")}
            </Button>
          )}
          <RangeDatePicker startDate={dateRange[0]} endDate={dateRange[1]} onChange={setDateRange} />
        </Box>
      </Box>
      <Box sx={styles.summaryCardsContainer}>
        {summaryCards.map((card) => (
          <SummaryCard key={card.title} {...card} />
        ))}
      </Box>
    </Box>
  );
};

export default JobsSummary;

const SummaryCard = ({
  icon,
  value,
  title,
  tooltip,
  background,
}: {
  icon: React.ReactNode;
  value: number;
  title: string;
  tooltip: string;
  background: string;
}) => {
  return (
    <Box sx={styles.summaryCardContainer} style={{ background }}>
      {icon}
      <Typography sx={styles.summaryCardValue}>{value}</Typography>
      <Box sx={styles.summaryCardTitleContainer}>
        <Typography sx={styles.summaryCardTitle}>{title}</Typography>
        <Tooltip title={tooltip} placement="top">
          <Box sx={styles.summaryCardTooltipContainer}>
            <ToolTipIcon />
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "24px",
    borderRadius: "12px",
    border: "1px solid var(--Gray-200, #EAECF0)",
    background: "var(--Base-White, #FFF)",
  },
  jobsSummaryContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  jobsSummaryTitle: {
    fontFamily: "Montserrat",
    color: "var(--black-100, #1C1C1C)",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "30px",
  },
  summaryCardsContainer: {
    display: "flex",
    gap: "16px",
    flexDirection: { xs: "column", sm: "row" },
    minHeight: "135px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
  },
  errorContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
  },
  summaryCardContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "16px",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    flex: 1,
  },
  summaryCardValue: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "30px",
  },
  summaryCardTitle: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  summaryCardTitleContainer: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  summaryCardTooltipContainer: {
    display: "inline-flex",
    cursor: "help",
  },
};
